 /**
 * Scenario Practice Component
 * Interactive scenario practice with AI evaluation
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { megaTrainerService } from '../../services/megaTrainerService';
import { puterCoachingService } from '../../services/puterCoachingService';
import Icon from '../../components/AppIcon';
import LoadingState from '../../components/ui/LoadingState';

const ScenarioPractice = () => {
  const { moduleId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [response, setResponse] = useState('');
  const [evaluating, setEvaluating] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const lessonNumber = Number(location.state?.lessonNumber || 1);

  useEffect(() => {
    loadScenarios();
  }, [moduleId, user?.id]);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      // Load the specific scenario for this lesson from trainer_scenarios table
      const scenario = await megaTrainerService.getScenarioForLesson(
        moduleId,
        lessonNumber
      );

      if (scenario) {
        // Single real scenario from database
        setScenarios([scenario]);
        setScenarioIndex(0);
        setCurrentScenario(scenario);
      } else {
        // This should not happen once trainer_scenarios are seeded
        console.warn(`No scenario found for ${moduleId}, lesson ${lessonNumber}`);
        setScenarios([]);
        setCurrentScenario(null);
      }
    } catch (error) {
      console.error('Error loading scenario:', error);
      setScenarios([]);
      setCurrentScenario(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluate = async () => {
    if (!response.trim()) {
      alert('Please enter your response');
      return;
    }

    try {
      setEvaluating(true);

      const prompt = `You are a sales coaching evaluator. A sales rep responded to this objection:
      
Scenario: ${currentScenario.scenario_context}
Objection: ${currentScenario.initial_objection}
Success Criteria: ${currentScenario.success_criteria}

Sales Rep Response: "${response}"

Provide evaluation in JSON format:
{
  "score": 0-100,
  "strengths": ["what they did well"],
  "improvements": ["what to work on"],
  "feedback": "coaching feedback",
  "alternative": "better way to respond"
}`;

      const result = await puterCoachingService.evaluateDrillResponse({
        scenario: currentScenario.scenario_context,
        prompt,
        userResponse: response,
        objectiveType: 'sales_scenario',
        rubric: {
          clarity: 'Was the response clear and concise?',
          effectiveness: 'Did it address the objection?',
          tone: 'Was the tone appropriate and professional?',
          technique: 'Did they apply the framework technique?'
        }
      });

      setEvaluation(result);
      await persistBestScore(moduleId, lessonNumber, Number(result?.score || 0));
    } catch (error) {
      console.error('Error evaluating scenario:', error);
      // Fallback evaluation
      const fallbackScore = 75;
      setEvaluation({
        score: 75,
        strengths: ['Good acknowledgment', 'Professional tone'],
        improvements: ['Add a sharper probe question', 'Be more concise'],
        feedback: 'You addressed the concern but could probe deeper with a specific question.',
        alternative: currentScenario.example_response || 'Try: If budget wasn\'t the issue, what would be the deciding factor?'
      });
      await persistBestScore(moduleId, lessonNumber, fallbackScore);
    } finally {
      setEvaluating(false);
    }
  };

  const persistBestScore = async (modId, lessonNum, score) => {
    const key = `trainer_best_score_${modId}_${lessonNum}`;
    const existing = Number(localStorage.getItem(key));
    const best = Number.isFinite(existing) ? Math.max(existing, score) : score;
    localStorage.setItem(key, String(best));

    if (user?.id) {
      await megaTrainerService.setLessonPracticeScore(user.id, modId, lessonNum, best);
    }
  };

  const handleNextScenario = () => {
    const nextIndex = scenarioIndex + 1;
    if (nextIndex < scenarios.length) {
      setScenarioIndex(nextIndex);
      setCurrentScenario(scenarios[nextIndex]);
      setResponse('');
      setEvaluation(null);
    } else {
      navigate('/app/mega-trainer');
    }
  };

  if (loading) {
    return <LoadingState message="Loading scenarios..." />;
  }

  if (!currentScenario) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No scenarios available</p>
        <button
          onClick={() => navigate('/app/mega-trainer')}
          className="px-4 py-2 bg-primary text-white rounded-lg"
        >
          Back to Trainer
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Breadcrumb */}
        <button
          onClick={() => navigate('/app/mega-trainer')}
          className="text-sm text-muted-foreground hover:text-foreground mb-6 flex items-center gap-2"
        >
          <Icon name="ArrowLeft" size={16} />
          Back to Trainer
        </button>

        {/* Progress */}
        <div className="mb-8">
          <p className="text-sm text-muted-foreground font-medium">
            Scenario {scenarioIndex + 1} of {scenarios.length}
          </p>
        </div>

        {/* Scenario Card */}
        <div className="bg-card rounded-xl border border-border overflow-hidden mb-8">

          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-8 border-b border-border">
            <h1 className="text-3xl font-bold text-foreground mb-4">{currentScenario.title}</h1>
            <p className="text-muted-foreground mb-4">{currentScenario.scenario_context}</p>
            <div className="flex items-center gap-3">
              <span className="text-sm px-3 py-1 bg-primary/20 text-primary rounded-full font-medium">
                {currentScenario.buyer_persona || 'Decision Maker'}
              </span>
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${
                currentScenario.difficulty <= 2 ? 'bg-success/20 text-success' :
                currentScenario.difficulty === 3 ? 'bg-warning/20 text-warning' :
                'bg-error/20 text-error'
              }`}>
                {currentScenario.difficulty <= 2 ? 'Beginner' : currentScenario.difficulty === 3 ? 'Intermediate' : 'Advanced'}
              </span>
            </div>
          </div>

          {/* Objection */}
          <div className="p-8 bg-error/5 border-b border-border">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="AlertCircle" className="w-5 h-5 text-error" />
              The Objection
            </h3>
            <div className="bg-card rounded-lg p-6 border border-border">
              <p className="text-foreground italic text-lg">"{currentScenario.initial_objection}"</p>
            </div>
          </div>

          {/* Success Criteria */}
          <div className="p-8 bg-success/5 border-b border-border">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Target" className="w-5 h-5 text-success" />
              Success Criteria
            </h3>
            <p className="text-foreground">{currentScenario.success_criteria}</p>
          </div>

          {/* Response Input */}
          {!evaluation && (
            <div className="p-8">
              <label className="block mb-4">
                <span className="text-sm font-semibold text-foreground mb-2 block">Your Response</span>
                <textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Type your response to this objection..."
                  className="w-full min-h-32 px-4 py-3 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={evaluating}
                />
              </label>
              <button
                onClick={handleEvaluate}
                disabled={evaluating || !response.trim()}
                className={`w-full px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  evaluating || !response.trim()
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90 text-white'
                }`}
              >
                {evaluating ? (
                  <>
                    <Icon name="Loader" size={18} className="animate-spin" />
                    Evaluating...
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={18} />
                    Get AI Feedback
                  </>
                )}
              </button>
            </div>
          )}

          {/* Evaluation Results */}
          {evaluation && (
            <div className="p-8">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-foreground">Your Score</h3>
                  <div className={`text-5xl font-bold ${
                    evaluation.score >= 80 ? 'text-success' :
                    evaluation.score >= 70 ? 'text-warning' :
                    'text-error'
                  }`}>
                    {evaluation.score}
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      evaluation.score >= 80 ? 'bg-success' :
                      evaluation.score >= 70 ? 'bg-warning' :
                      'bg-error'
                    }`}
                    style={{ width: `${evaluation.score}%` }}
                  />
                </div>
              </div>

              {/* Strengths */}
              {evaluation.strengths && evaluation.strengths.length > 0 && (
                <div className="mb-6 p-4 bg-success/10 rounded-lg border border-success/20">
                  <h4 className="font-semibold text-success mb-2 flex items-center gap-2">
                    <Icon name="CheckCircle" size={18} />
                    What You Did Well
                  </h4>
                  <ul className="space-y-2">
                    {evaluation.strengths.map((strength, idx) => (
                      <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                        <span className="text-success mt-1">✓</span>
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {evaluation.improvements && evaluation.improvements.length > 0 && (
                <div className="mb-6 p-4 bg-warning/10 rounded-lg border border-warning/20">
                  <h4 className="font-semibold text-warning mb-2 flex items-center gap-2">
                    <Icon name="AlertCircle" size={18} />
                    Areas to Focus On
                  </h4>
                  <ul className="space-y-2">
                    {evaluation.improvements.map((improvement, idx) => (
                      <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                        <span className="text-warning mt-1">→</span>
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Feedback */}
              {evaluation.feedback && (
                <div className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <h4 className="font-semibold text-primary mb-2">Coaching Feedback</h4>
                  <p className="text-sm text-foreground">{evaluation.feedback}</p>
                </div>
              )}

              {/* Alternative */}
              {evaluation.alternative && (
                <div className="mb-6 p-4 bg-secondary/10 rounded-lg border border-secondary/20">
                  <h4 className="font-semibold text-secondary mb-2">Better Approach</h4>
                  <p className="text-sm text-foreground italic">"{evaluation.alternative}"</p>
                </div>
              )}

              {/* Next Action */}
              <button
                onClick={handleNextScenario}
                className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <Icon name="ArrowRight" size={18} />
                {scenarioIndex + 1 < scenarios.length ? 'Next Scenario' : 'Back to Trainer'}
              </button>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default ScenarioPractice;
